import { useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { WAVE_SESSIONS, PARTICIPANT_ARMS } from "../queries/waves";
import { formatDateSGT, formatTimeSGT } from "./dateFormat";

const PAGE_SIZE = 25;

function ParticipantsDrawer({ waveId, sessionId, onClose }) {
    const { loading, data, error } = useQuery(PARTICIPANT_ARMS, { variables: { waveId, sessionId } });
    return (
        <div className="admin-modal-bg" onClick={onClose}>
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
                <h3>Session {sessionId}: participant arms</h3>
                {loading && <p>Loading…</p>}
                {error && <p style={{color:"crimson"}}>{error.message}</p>}
                {data && (
                    <table className="admin-table">
                        <thead><tr><th>Participant</th><th>Arm</th><th>Status</th></tr></thead>
                        <tbody>
                            {data.participantArms.length === 0 && (
                                <tr><td colSpan={3} style={{color:"#888", textAlign:"center"}}>No arm assignments yet.</td></tr>
                            )}
                            {data.participantArms.map((p, i) => (
                                <tr key={`${p.participant_id}-${i}`}>
                                    <td>{p.participant_id}</td><td>{p.arm}</td><td>{p.retained_or_reassigned}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <div style={{textAlign:"right", marginTop:12}}>
                    <button className="admin-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

export default function SessionsTab({ waveId, refreshKey = 0 }) {
    const [offset, setOffset] = useState(0);
    const [activeSession, setActiveSession] = useState(null);
    const { loading, data, error, refetch } = useQuery(WAVE_SESSIONS, {
        variables: { waveId, limit: PAGE_SIZE, offset },
    });

    // A PING fired (refreshKey bumped by the parent) — reload the session list.
    // Skip the initial render since useQuery already fetched.
    const firstRun = useRef(true);
    useEffect(() => {
        if (firstRun.current) { firstRun.current = false; return; }
        refetch();
    }, [refreshKey, refetch]);

    return (
        <div className="admin-section">
            <h3>Sessions</h3>
            {loading && <p>Loading…</p>}
            {error && <p style={{color:"crimson"}}>{error.message}</p>}
            {data && (
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Session ID</th><th>Phase</th>
                            <th>Start Date</th><th>Start Time</th>
                            <th>End Date</th><th>End Time</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.waveSessions.length === 0 && (
                            <tr><td colSpan={7} style={{color:"#888", textAlign:"center"}}>No sessions yet.</td></tr>
                        )}
                        {data.waveSessions.map(s => (
                            <tr key={s.session_id}>
                                <td>{s.session_id}</td>
                                <td>{s.phase}</td>
                                <td>{formatDateSGT(s.window_start)}</td>
                                <td>{formatTimeSGT(s.window_start)}</td>
                                <td>{formatDateSGT(s.window_end)}</td>
                                <td>{formatTimeSGT(s.window_end)}</td>
                                <td><button className="admin-btn" onClick={() => setActiveSession(s.session_id)}>View arms</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <div style={{marginTop:12}}>
                <button className="admin-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
                <button className="admin-btn" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
            </div>

            {activeSession != null && (
                <ParticipantsDrawer waveId={waveId} sessionId={activeSession} onClose={() => setActiveSession(null)} />
            )}
        </div>
    );
}
